<template>
  <div id="vesselAdminView" class="text-left">
    <div class="row">
      <div class="col">
        <h4>manage vessels</h4>
        <div class="d-flex justify-content-end clearfix mb-4">
          <button type="button" class="btn btn-primary " @click="addVessel"><i class="icon-expand-plus" title="add new vessel"></i></button>
        </div>
        <transition name="slide-fade">
          <app-alert v-if="alert.message" :type="alert.type" :message="alert.message" @alertclose="closeMessage" />
        </transition>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <app-modal :show="showAddEditModal" :title="vessel.id ? 'edit vessel' : 'add new vessel'">
          <ul class="error">
            <li v-for="error in errors">
              <span class="font-weight-bold">{{ error.title }}</span>: {{ error.message }}
            </li>
          </ul>

          <form id="vesselEntryForm">
            <VesselDataEntry :vessel="vessel" :isFormDisabled="isSavingVessel"></VesselDataEntry>
          </form>
          <template v-slot:footer>
            <button class="btn btn-danger d-inline mr-2" @click.prevent="cancelAddOrEdit" v-bind:disabled="isSavingVessel">Cancel</button>
            <button class="btn btn-primary d-inline mr-2" v-bind:class="{ spin: isSavingVessel }" @click.prevent="saveVessel" v-bind:disabled="isSavingVessel">Submit<span class="spinner"></span></button>
          </template>
        </app-modal>
        <app-modal :show="showChangeStatusModal" title="remove vessel">
          <ul class="error">
            <li v-for="error in errors">
              <span class="font-weight-bold">{{ error.title }}</span>: {{ error.message }}
            </li>
          </ul>
          <form>
            <fieldset v-bind:disabled="isChangingVesselStatus">
              <span class="font-weight-bold">Are you sure you want to {{ vessel.status === Enums.Status.Active.value ? 'disable' : 'enable' }}&nbsp;{{ vessel.name }} ?</span>
            </fieldset>
          </form>
          <template v-slot:footer>
            <button class="btn btn-danger d-inline mr-2" @click.prevent="cancelDisable" v-bind:disabled="isChangingVesselStatus">Cancel</button>
            <button class="btn btn-primary d-inline mr-2" v-bind:class="{ spin: isChangingVesselStatus }" @click.prevent="changeVesselStatus" v-bind:disabled="isChangingVesselStatus">{{ vessel.status === Enums.Status.Active.value ? 'Disable' : 'Enable' }}<span class="spinner"></span></button>
          </template>
        </app-modal>
        <VesselList ref="vesselList" :tableProps="vesselListProps" :id="'vesselList'+ currentTableKeyIndex" :key="'vesselList-'+ currentTableKeyIndex" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

  .slide-fade-enter-active {
    transition: all .5s ease;
  }

  .slide-fade-leave-active {
    transition: all .5s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
    transform: translateX(10px);
    opacity: 0;
  }

  button {
    position: relative;
    transition: all 1s;
  }

  .spin {
    padding-left: 2.5em;
    display: block;
  }

  .spin .spinner {
    left: -.6em;
    top: .4em;
    width: 2.5em;
    display: block;
    position: absolute;
  }

  /* spinner animation */
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  /* The actual spinner element is a pseudo-element */
  .spin .spinner::before {
    content: "";
    width: 1.5em; /* Size of the spinner */
    height: 1.5em; /* Change as desired */
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    border: solid .35em #000; /* Thickness/color of spinner track */
    border-bottom-color: #555; /* Color of variant spinner piece */
    animation: .8s linear infinite spinner; /* speed of spinner */
    transform: translate(-50%, -50%);
    will-change: transform;
  }

  /* optional, but it will affect the size if changed */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  input:invalid {
    border: 1px solid red !important;
  }
</style>

<script>
  import store from '@/store'
  import { mapState } from 'vuex'

  import eventBus from '@/utils/eventBus'
  import Events from '@/utils/events'
  import Enums from '@/utils/enums'

  import { FETCH_VESSELS, CREATE_VESSEL, UPDATE_VESSEL, UPDATE_VESSEL_STATUS, FETCH_VESSELTYPES  } from '@/store/action-type'
  import { CLOSE_MODAL_EVENT, EDIT_VESSEL_EVENT, CHANGE_VESSEL_STATUS_EVENT } from '@/utils/constants'

  import AppAlert from '@/components/ui/AppAlert'
  import AppModal from '@/components/ui/AppModal'
  import VesselDataEntry from '@/components/admin/vessel/EntryForm'
  import VesselList from '@/components/admin/vessel/VesselList'

  import AlertMixin from '@/mixins/AlertMixin'
  import ErrorMixin from '@/mixins/ErrorMixin'

  export default {
    name: 'VesselView',
    components: {
      AppAlert,
      AppModal,
      VesselDataEntry,
      VesselList
    },
    mixins: [AlertMixin, ErrorMixin],
    data: () => ({
      vessel: {
        id: null,
        name: '',
        grt: '',
        vesselTypeId: '',
        status: Enums.Status.Active.value,
        version: null
      },
      vesselListProps: {
        order: null,
        page: null,
        search: null,
        layout: 'list'
      },
      currentTableKeyIndex: 1,
      showAddEditModal: false,
      showChangeStatusModal: false,
      isSavingVessel: false,
      isChangingVesselStatus: false,
      Enums
    }),
    computed: {
      ...mapState([
        'Vessels'
      ])
    },
    watch: {
      showAddEditModal(value) {
        if (value) {
          this.$nextTick(() => {
            document.getElementById('vesselName').focus()
          })
        }
      }
    },
    methods: {
      addVessel() {
        this.vessel.id = null
        this.vessel.name = ''
        this.vessel.grt = ''
        this.vessel.vesselTypeId = ''
        this.vessel.status = Enums.Status.Active.value
        this.showAddEditModal = true
      },
      editVessel(vessel) {
        this.vessel.id = vessel.id
        this.vessel.name = vessel.name
        this.vessel.grt = vessel.grt.toString()
        this.vessel.vesselTypeId = vessel.vesselTypeId
        this.vessel.status = vessel.status
        this.vessel.version = vessel.version
        this.showAddEditModal = true
      },
      onChangeVesselStatus(vessel) {
        this.vessel.id = vessel.id
        this.vessel.name = vessel.name
        this.vessel.status = vessel.status
        this.showChangeStatusModal = true
      },
      saveVessel() {
        if (this.validateAddForm()) {
          const self = this
          self.isSavingVessel = true
          self.closeMessage()
          let vessel = { ...self.vessel }
          const actionType = vessel.id ? UPDATE_VESSEL : CREATE_VESSEL
          store.dispatch(actionType, vessel).then(() => {
            self.showSuccessMessage(`vessel created successfully`)
            self.currentTableKeyIndex = self.currentTableKeyIndex + 1
            self.$nextTick(() => {
              self.$refs.vesselList.refresh();
            });
            self.showAddEditModal = false
          }).catch((error) => {
            self.errors.push({
              title: 'error',
              message: error
            })
          }).finally(() => {
            self.isSavingVessel = false
          })
        }
      },
      changeVesselStatus() {
        const self = this
        self.isChangingVesselStatus = true
        self.closeMessage()
        store.dispatch(UPDATE_VESSEL_STATUS, self.vessel).then(() => {
          self.showSuccessMessage(`status changed successfully`)
          self.currentTableKeyIndex = self.currentTableKeyIndex + 1
          self.$nextTick(() => {
            self.$refs.vesselList.refresh();
          });
          self.showChangeStatusModal = false
        }).catch((error) => {
          //self.handleError(error)
          self.errors.push({
            title: 'error',
            message: error
          })
        }).finally(() => {
          self.isChangingVesselStatus = false
        })
      },
      cancelAddOrEdit() {
        this.errors.splice(0, this.errors.length)
        this.showAddEditModal = false
      },
      cancelDisable() {
        this.showChangeStatusModal = false
        this.errors.splice(0, this.errors.length)
      },
      validateAddForm() {
        const self = this
        self.errors.splice(0, self.errors.length)
        let form = $('#vesselEntryForm')

        var invalidFields = form.find(":invalid").not('fieldset').each(function (index, node) {
          // Find the field's corresponding label
          var label = $("label[for=" + node.id + "] "),
            // Opera incorrectly does not fill the validationMessage property.
            message = node.validationMessage || 'Invalid value.';
          self.errors.push({
            title: label.text(),
            message: message
          })
        });
        return invalidFields.length == 0
      }
    },
    beforeRouteEnter(to, from, next) {
      eventBus.$emit(Events.LongOperationStarted, 'loading data')
      const tasks = []
      tasks.push(store.dispatch(FETCH_VESSELS))
      tasks.push(store.dispatch(FETCH_VESSELTYPES))
      Promise.all(tasks).then(() => {
        next()
      }).catch((error) => {
        next(vm => {
          vm.showErrorMessage(error)
        })
      }).finally(() => {
        eventBus.$emit(Events.LongOperationCompleted)
      })
    },
    mounted() {
      const self = this
      eventBus.$on(EDIT_VESSEL_EVENT, self.editVessel)
      eventBus.$on(CHANGE_VESSEL_STATUS_EVENT, self.onChangeVesselStatus)
      eventBus.$on(CLOSE_MODAL_EVENT, () => {
        this.errors.splice(0, this.errors.length)
        this.showAddEditModal = false
        this.showChangeStatusModal = false
      })
      eventBus.$on(CLOSE_MODAL_EVENT, () => {
        self.showAddEditModal = false
      })
    },
    beforeDestroy() {
      eventBus.$off(EDIT_VESSEL_EVENT)
      eventBus.$off(CHANGE_VESSEL_STATUS_EVENT)
      eventBus.$off(CLOSE_MODAL_EVENT)
    }
  }
</script>
